// ============================================================================
// TERMO REVOGACAO CLIENTE REPOSITORY
// ARQUIVO: src/services/TermoRevogacaoClienteRepository.js
// RESPONSABILIDADE: Operações Firestore do TermoRevogacaoClienteEngine.
// Espelha DeclarationEngineRepository.js, mas usa `public/data` (não
// `public/config` — inconsistência legada da Declaração, não repetida aqui).
// ============================================================================

import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../shared/api/firebase/config";
import { FIREBASE_CONFIG } from "../config/Config";

const APP_ID = FIREBASE_CONFIG?.projectId || "portalrda-f312d";
const TPL_DOC_ID = "tpl_revogacao_cliente_v1";
const TPL_COLLECTION = "mandate_termination_templates";

/** Carrega template do Termo de Revogação (snapshot único). */
export async function loadRevogacaoTemplate() {
  const tplDoc = await getDoc(
    doc(db, "artifacts", APP_ID, "public", "data", TPL_COLLECTION, TPL_DOC_ID),
  );
  return tplDoc.exists() ? tplDoc.data() : null;
}

/** Salva/sobrescreve o template do Termo de Revogação na nuvem (Mestre). */
export async function saveRevogacaoTemplate(payload) {
  const ref = doc(
    db,
    "artifacts",
    APP_ID,
    "public",
    "data",
    TPL_COLLECTION,
    TPL_DOC_ID,
  );
  const data = {
    structure: JSON.parse(JSON.stringify(payload?.structure || [])),
    updatedAt: serverTimestamp(),
    updatedBy: payload?.updatedBy || null,
  };
  const existing = await getDoc(ref);
  if (existing.exists()) {
    await updateDoc(ref, data);
  } else {
    await addDoc(
      collection(db, "artifacts", APP_ID, "public", "data", TPL_COLLECTION),
      { ...data, id: TPL_DOC_ID, createdAt: serverTimestamp() },
    );
  }
  return true;
}

/** Assina (tempo real) o template do Termo de Revogação. */
export function subscribeRevogacaoTemplate(callback) {
  const ref = doc(
    db,
    "artifacts",
    APP_ID,
    "public",
    "data",
    TPL_COLLECTION,
    TPL_DOC_ID,
  );
  return onSnapshot(ref, (snap) => {
    const data = snap.exists() ? snap.data() : null;
    callback(data && Array.isArray(data.structure) ? data : null);
  });
}

/** Atualiza estrutura de um Termo de Revogação já salvo (autosave). */
export async function updateRevogacaoDocumentStructure(
  docId,
  structure,
  processoRef,
  objetoMandato,
  actorName,
) {
  await updateDoc(
    doc(collection(db, "artifacts", APP_ID, "public", "data", "documents"), docId),
    {
      structure: JSON.parse(JSON.stringify(structure)),
      processoRef,
      objetoMandato,
      updatedAt: serverTimestamp(),
      updatedBy: actorName,
    },
  );
}

/** Busca workspace (caderno) do cliente. */
export async function findClientWorkspace(clientId) {
  const wsSnaps = await getDocs(
    query(
      collection(db, "artifacts", APP_ID, "public", "data", "autos_workspaces"),
      where("clientId", "==", clientId),
    ),
  );
  return !wsSnaps.empty ? wsSnaps.docs[0] : null;
}

/** Busca Termo de Revogação existente do cliente (mais recente). */
export async function findExistingRevogacao(clientId) {
  const snaps = await getDocs(
    query(
      collection(db, "artifacts", APP_ID, "public", "data", "documents"),
      where("clientId", "==", clientId),
      where("type", "==", "MANDATE_TERMINATION"),
      where("subtype", "==", "REVOGACAO_CLIENTE"),
    ),
  );
  if (snaps.empty) return null;
  const sorted = snaps.docs
    .map((d) => ({ id: d.id, data: d.data() }))
    .sort((a, b) => {
      const ta = a.data?.updatedAt?.toMillis?.() || 0;
      const tb = b.data?.updatedAt?.toMillis?.() || 0;
      return tb - ta;
    });
  return sorted[0];
}

/** Salva Termo de Revogação (cria ou atualiza). */
export async function saveRevogacao(payload, clientId, existingId) {
  const docsCol = collection(db, "artifacts", APP_ID, "public", "data", "documents");
  let docId = existingId;
  const enriched = { ...payload, updatedAt: payload.updatedAt || serverTimestamp() };

  if (docId) {
    await updateDoc(doc(docsCol, docId), enriched);
  } else {
    const existingSnap = await getDocs(
      query(
        docsCol,
        where("clientId", "==", clientId),
        where("type", "==", "MANDATE_TERMINATION"),
        where("subtype", "==", "REVOGACAO_CLIENTE"),
      ),
    );
    if (!existingSnap.empty) {
      const sorted = existingSnap.docs
        .map((d) => ({ id: d.id, ts: d.data()?.updatedAt?.toMillis?.() || 0 }))
        .sort((a, b) => b.ts - a.ts);
      docId = sorted[0].id;
      await updateDoc(doc(docsCol, docId), payload);
    } else {
      const ref = await addDoc(docsCol, {
        ...payload,
        id: "",
        createdAt: serverTimestamp(),
        publicationVersion: 0,
      });
      await updateDoc(ref, { id: ref.id });
      docId = ref.id;
    }
  }
  return docId;
}

/** Publica Termo de Revogação (gera visibilidade no Portal do Cliente). */
export async function publishRevogacao(docId, data) {
  const docsCol = collection(db, "artifacts", APP_ID, "public", "data", "documents");
  await updateDoc(doc(docsCol, docId), {
    ...data,
    updatedAt: data.updatedAt || serverTimestamp(),
  });
}

/** Exclui publicação do acervo. */
export async function deleteRevogacaoPublication(docId) {
  const docsCol = collection(db, "artifacts", APP_ID, "public", "data", "documents");
  await deleteDoc(doc(docsCol, docId));
}
